import { useEffect, useRef, useState } from 'react'

// Filter sidebar for /products. Every group is driven by the `facets` block of
// /product/search, so a group with nothing to offer (no tags in the catalogue,
// no reviewed products yet) simply doesn't render. Counts are disjunctive: each
// group's numbers ignore its own selection, so ticking one grade never hides
// the others.

export const formatINR = (n) => `₹${Math.round(Number(n)).toLocaleString('en-IN')}`

const PRODUCT_TYPE_LABELS = {
  gems: 'Gemstones (by weight)',
  simple: 'Standard items',
  attribute: 'With options',
}
export const productTypeLabel = (v) => PRODUCT_TYPE_LABELS[v] ?? v

function Section({ title, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="py-5 border-b border-dashed border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 text-xs font-bold tracking-[0.2em] text-primary hover:text-secondary transition-colors"
      >
        <span className="flex items-center gap-2">
          {title}
          {badge > 0 && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-secondary text-white">{badge}</span>
          )}
        </span>
        <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  )
}

function CheckOption({ checked, disabled, onChange, label, count }) {
  return (
    <label
      className={`flex items-center gap-3 py-1.5 cursor-pointer group normal-case ${disabled && !checked ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled && !checked}
        onChange={onChange}
      />
      <span
        className={`h-4 w-4 shrink-0 rounded-md border flex items-center justify-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-secondary/40 ${checked ? 'bg-secondary border-secondary' : 'bg-white border-gray-300 group-hover:border-secondary'}`}
      >
        {checked && <i className="fa-solid fa-check text-[8px] text-white"></i>}
      </span>
      <span className={`flex-1 text-[15px] font-body leading-snug ${checked ? 'text-primary' : 'text-primary/90'}`}>{label}</span>
      {count !== undefined && <span className="text-xs font-mono text-primary/60">{count}</span>}
    </label>
  )
}

function ToggleOption({ checked, onChange, label, count }) {
  const disabled = !checked && count === 0
  return (
    <label className={`flex items-center justify-between gap-3 py-2 cursor-pointer normal-case ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
      <span className="text-[15px] font-body text-primary/90">
        {label}
        {count !== undefined && <span className="ml-2 text-xs font-mono text-primary/60">{count}</span>}
      </span>
      <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={onChange} />
      <span
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-secondary/40 ${checked ? 'bg-secondary' : 'bg-gray-200'}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? 'left-4.5' : 'left-0.5'}`}></span>
      </span>
    </label>
  )
}

function ChipOption({ active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      disabled={disabled && !active}
      onClick={onClick}
      className={`h-8 px-3 rounded-lg text-[11px] font-bold tracking-widest border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${active
        ? 'bg-primary text-white border-primary'
        : 'bg-white border-gray-200 text-primary/90 hover:border-secondary hover:text-secondary'
        }`}
    >
      {children}
    </button>
  )
}

// Dual-thumb slider plus two number boxes. Edits stay local and are committed
// (debounced) to the URL, so dragging doesn't fire a request per pixel. A bound
// left at the edge of the range is committed as "no limit".
const COMMIT_DELAY_MS = 500

function RangeFilter({ bounds, value, step, format, onCommit }) {
  // toFixed strips float noise (Math.floor(3.76 / 0.1) * 0.1 = 3.7000000000000002).
  const decimals = step < 1 ? String(step).split('.')[1].length : 0
  const floor = Number((Math.floor(bounds.min / step) * step).toFixed(decimals))
  const ceil = Number((Math.ceil(bounds.max / step) * step).toFixed(decimals))
  const clamp = (v) => Math.min(ceil, Math.max(floor, v))
  const [lo, setLo] = useState(clamp(value.min ?? floor))
  const [hi, setHi] = useState(clamp(value.max ?? ceil))
  const dirty = useRef(false)

  // URL/bounds changed elsewhere (pill removed, reset) → resync.
  useEffect(() => {
    dirty.current = false
    setLo(clamp(value.min ?? floor))
    setHi(clamp(value.max ?? ceil))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.min, value.max, floor, ceil])

  useEffect(() => {
    if (!dirty.current) return
    const t = setTimeout(() => {
      dirty.current = false
      const a = Math.min(lo, hi)
      const b = Math.max(lo, hi)
      onCommit({ min: a > floor ? a : undefined, max: b < ceil ? b : undefined })
    }, COMMIT_DELAY_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lo, hi])

  const edit = (setter) => (v) => {
    dirty.current = true
    setter(v)
  }
  const setLoEdit = edit(setLo)
  const setHiEdit = edit(setHi)
  const span = ceil - floor || 1
  const left = ((Math.min(lo, hi) - floor) / span) * 100
  const right = 100 - ((Math.max(lo, hi) - floor) / span) * 100

  return (
    <div className="space-y-4">
      <div className="dual-range">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-gray-200"></div>
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-secondary"
          style={{ left: `${left}%`, right: `${right}%` }}
        ></div>
        <input
          type="range" min={floor} max={ceil} step={step} value={lo}
          aria-label="Minimum"
          onChange={e => setLoEdit(Math.min(Number(e.target.value), hi))}
        />
        <input
          type="range" min={floor} max={ceil} step={step} value={hi}
          aria-label="Maximum"
          onChange={e => setHiEdit(Math.max(Number(e.target.value), lo))}
        />
      </div>
      <div className="flex items-center gap-2">
        {[['MIN', lo, setLoEdit], ['MAX', hi, setHiEdit]].map(([label, v, set], i) => (
          <div key={label} className="flex-1 flex items-center gap-2">
            {i === 1 && <span className="text-primary/40 text-sm">–</span>}
            <label className="flex-1">
              <span className="block text-[11px] font-mono tracking-widest text-primary/65 mb-1">{label}</span>
              <input
                type="number"
                inputMode="decimal"
                min={floor}
                max={ceil}
                step={step}
                value={v}
                onChange={e => {
                  const n = Number(e.target.value)
                  if (e.target.value !== '' && Number.isFinite(n)) set(clamp(n))
                }}
                className="w-full h-9 px-2 bg-gray-50/50 border border-gray-100 rounded-lg text-sm font-mono text-primary focus:border-secondary/40 focus:ring-2 focus:ring-secondary/20 outline-none"
              />
            </label>
          </div>
        ))}
      </div>
      <p className="text-xs font-mono tracking-wider text-primary/65 normal-case">
        {format(floor)} – {format(ceil)}
      </p>
    </div>
  )
}

function SkeletonPanel() {
  return (
    <div className="space-y-6 py-4 animate-pulse">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="space-y-3">
          <div className="h-3 w-24 rounded bg-gray-200" />
          <div className="h-3 w-full rounded bg-gray-100" />
          <div className="h-3 w-3/4 rounded bg-gray-100" />
        </div>
      ))}
    </div>
  )
}

const toggleIn = (list, v) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v])

export default function FilterPanel({
  facets,
  filters,
  subOptions,
  selectedSubIds,
  onToggleSub,
  onUpdate,
  onReset,
  activeCount,
}) {
  if (!facets) return <SkeletonPanel />

  const hasRange = (r) => r && r.min !== null && r.max !== null && r.max > r.min
  const discounts = facets.discounts.filter(d => d.count > 0 || filters.discount === d.min)
  const ratings = facets.ratings.filter(r => r.count > 0 || filters.rating === r.min)

  return (
    <div>
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <span className="text-[13px] font-bold tracking-[0.25em] text-primary flex items-center gap-2">
          <i className="fa-solid fa-sliders text-secondary"></i> REFINE
        </span>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            className="text-[11px] font-bold tracking-widest text-secondary hover:text-primary transition-colors"
          >
            CLEAR ALL ({activeCount})
          </button>
        )}
      </div>

      {subOptions.length > 0 && (
        <Section title="COLLECTION" badge={selectedSubIds.length}>
          <div className="max-h-64 overflow-y-auto scrollbar-theme pr-1 -mr-1">
            {subOptions.map(c => (
              <CheckOption
                key={c.id}
                checked={selectedSubIds.includes(c.id)}
                disabled={c.count === 0}
                onChange={() => onToggleSub(c.id)}
                label={c.name}
                count={c.count}
              />
            ))}
          </div>
        </Section>
      )}

      {hasRange(facets.price) && (
        <Section title="PRICE" badge={filters.minPrice !== undefined || filters.maxPrice !== undefined ? 1 : 0}>
          <RangeFilter
            bounds={facets.price}
            value={{ min: filters.minPrice, max: filters.maxPrice }}
            step={100}
            format={formatINR}
            onCommit={({ min, max }) => onUpdate({ min_price: min, max_price: max })}
          />
        </Section>
      )}

      <Section title="AVAILABILITY & OFFERS">
        <ToggleOption
          label="In stock only"
          checked={filters.inStock}
          count={facets.flags.in_stock}
          onChange={() => onUpdate({ in_stock: filters.inStock ? null : 1 })}
        />
        <ToggleOption
          label="On sale"
          checked={filters.onSale}
          count={facets.flags.on_sale}
          onChange={() => onUpdate({ on_sale: filters.onSale ? null : 1 })}
        />
        <ToggleOption
          label="Featured picks"
          checked={filters.featured}
          count={facets.flags.featured}
          onChange={() => onUpdate({ featured: filters.featured ? null : 1 })}
        />
        <ToggleOption
          label="Special collection"
          checked={filters.special}
          count={facets.flags.special}
          onChange={() => onUpdate({ special: filters.special ? null : 1 })}
        />
      </Section>

      {discounts.length > 0 && (
        <Section title="DISCOUNT" badge={filters.discount ? 1 : 0}>
          <div className="flex flex-wrap gap-2">
            {discounts.map(d => (
              <ChipOption
                key={d.min}
                active={filters.discount === d.min}
                disabled={d.count === 0}
                onClick={() => onUpdate({ discount: filters.discount === d.min ? null : d.min })}
              >
                {d.min}%+ OFF <span className="ml-1 font-mono opacity-60">{d.count}</span>
              </ChipOption>
            ))}
          </div>
        </Section>
      )}

      {facets.grades.length > 0 && (
        <Section title="QUALITY GRADE" badge={filters.grades.length}>
          <div className="grid grid-cols-2 gap-x-3">
            {facets.grades.map(g => (
              <CheckOption
                key={g.id}
                checked={filters.grades.includes(g.id)}
                disabled={g.count === 0}
                onChange={() => onUpdate({ grade: toggleIn(filters.grades, g.id) })}
                label={g.name}
                count={g.count}
              />
            ))}
          </div>
        </Section>
      )}

      {facets.origins.length > 0 && (
        <Section title="ORIGIN" badge={filters.origins.length}>
          {facets.origins.map(o => (
            <CheckOption
              key={o.id}
              checked={filters.origins.includes(o.id)}
              disabled={o.count === 0}
              onChange={() => onUpdate({ origin: toggleIn(filters.origins, o.id) })}
              label={o.name}
              count={o.count}
            />
          ))}
        </Section>
      )}

      {hasRange(facets.ratti) && (
        <Section title="WEIGHT (RATTI)" defaultOpen={filters.minRatti !== undefined || filters.maxRatti !== undefined} badge={filters.minRatti !== undefined || filters.maxRatti !== undefined ? 1 : 0}>
          <RangeFilter
            bounds={facets.ratti}
            value={{ min: filters.minRatti, max: filters.maxRatti }}
            step={0.1}
            format={(v) => `${Number(v).toFixed(1)} ratti`}
            onCommit={({ min, max }) => onUpdate({ min_ratti: min, max_ratti: max })}
          />
        </Section>
      )}

      {hasRange(facets.carat) && (
        <Section title="WEIGHT (CARAT)" defaultOpen={filters.minCarat !== undefined || filters.maxCarat !== undefined} badge={filters.minCarat !== undefined || filters.maxCarat !== undefined ? 1 : 0}>
          <RangeFilter
            bounds={facets.carat}
            value={{ min: filters.minCarat, max: filters.maxCarat }}
            step={0.1}
            format={(v) => `${Number(v).toFixed(1)} ct`}
            onCommit={({ min, max }) => onUpdate({ min_carat: min, max_carat: max })}
          />
        </Section>
      )}

      {facets.product_types.length > 1 && (
        <Section title="PRODUCT TYPE" badge={filters.types.length} defaultOpen={filters.types.length > 0}>
          {facets.product_types.map(t => (
            <CheckOption
              key={t.value}
              checked={filters.types.includes(t.value)}
              disabled={t.count === 0}
              onChange={() => onUpdate({ type: toggleIn(filters.types, t.value) })}
              label={productTypeLabel(t.value)}
              count={t.count}
            />
          ))}
        </Section>
      )}

      {ratings.length > 0 && (
        <Section title="CUSTOMER RATING" badge={filters.rating ? 1 : 0}>
          <div className="flex flex-wrap gap-2">
            {ratings.map(r => (
              <ChipOption
                key={r.min}
                active={filters.rating === r.min}
                disabled={r.count === 0}
                onClick={() => onUpdate({ rating: filters.rating === r.min ? null : r.min })}
              >
                {r.min}<i className="fa-solid fa-star text-[8px] mx-1"></i>& UP
                <span className="ml-1 font-mono opacity-60">{r.count}</span>
              </ChipOption>
            ))}
          </div>
        </Section>
      )}

      {facets.tags.length > 0 && (
        <Section title="TAGS" badge={filters.tags.length} defaultOpen={filters.tags.length > 0}>
          <div className="flex flex-wrap gap-2">
            {facets.tags.map(t => (
              <ChipOption
                key={t.id}
                active={filters.tags.includes(t.id)}
                disabled={t.count === 0}
                onClick={() => onUpdate({ tag: toggleIn(filters.tags, t.id) })}
              >
                {t.name} <span className="ml-1 font-mono opacity-60">{t.count}</span>
              </ChipOption>
            ))}
          </div>
        </Section>
      )}

      {facets.attributes.map(attr => {
        const selected = attr.values.filter(v => filters.attrs.includes(v.id)).length
        return (
          <Section key={attr.id} title={attr.name.toUpperCase()} badge={selected} defaultOpen={selected > 0}>
            {attr.values.map(v => (
              <CheckOption
                key={v.id}
                checked={filters.attrs.includes(v.id)}
                disabled={v.count === 0}
                onChange={() => onUpdate({ attr: toggleIn(filters.attrs, v.id) })}
                label={v.value}
                count={v.count}
              />
            ))}
          </Section>
        )
      })}
    </div>
  )
}
